import { useMemo } from 'react';
import { Ray, Vector2Tuple } from 'three';

import { GroupProps } from '@react-three/fiber';
import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { MeasurementControls, useSelection } from '@spazious/dollhouse-engine';
import { FNode, getNodeFromObject } from '@spazious/dollhouse-fsetup';
import { useStaging, useStore } from '@spazious/dollhouse-store';
import { ControlsModeEnum, MeasurementStackEnum } from '@spazious/dollhouse-types';

import { useUnitConverter } from '../../index.js';
import { useFeatures } from '../Features/index.js';
const ray = new Ray();

ray.direction.set(0, 0, 1);

export interface MeasurementProps extends GroupProps {
  points?: Vector2Tuple[];
  fcarpet?: number;
  attrs?: Record<string, any>;
  selector: string;
}

export const Measurement: React.FC<MeasurementProps> = ({
  points = [],
  selector,
  ...restProps
}) => {
  const { staging, createAction, update, commit } = useStaging();
  const { controls, set, measurements } = useStore();
  const { select, selected } = useSelection();
  const { hasFeature } = useFeatures();
  const { displayUnit } = useUnitConverter();
  const canEditMeasurements =
    controls === ControlsModeEnum.Zenithal && hasFeature('graphs.setup.node.measurement:editable');

  const isSelected = useMemo(() => {
    return selected.some((object) => {
      const graphNode = getNodeFromObject(object) as FNode | null;
      return graphNode?.selector === selector;
    });
  }, [selected, selector]);

  const labelFormatter = (value: string | number) => {
    const showUnit = true;
    return displayUnit(value, showUnit);
  };

  return (
    <group
      {...restProps}
      onPointerUp={(evt) => {
        if (measurements.drawEnabled) return;
        evt.stopPropagation();
        set({ measurements: { ...measurements, stack: MeasurementStackEnum.Delete } });
        select([evt.eventObject]);
      }}
    >
      {points.length && (
        <MeasurementControls
          color="orange"
          editable={canEditMeasurements}
          labelFormatter={labelFormatter}
          outline={isSelected}
          points={points}
          onChange={({ points, type, last }) => {
            if (measurements.drawEnabled) return;
            switch (type) {
              case 'add':
                staging.add(createAction<[number, number][]>('update', selector, 'points', points));
                staging.commit(CommanderActionTypesEnum.Move);
                break;
              case 'remove':
                staging.add(createAction<[number, number][]>('update', selector, 'points', points));
                staging.commit(CommanderActionTypesEnum.Remove);
                break;
              case 'update':
                update(selector, 'points', points);
                if (last) {
                  commit(CommanderActionTypesEnum.Move);
                }
                break;
            }
          }}
        />
      )}
    </group>
  );
};
